import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Share2, Check } from "lucide-react";
import Seo, { jsonLdHerramienta, SITIO } from "../components/Seo.jsx";
import AdSlot, { RANURAS } from "../components/AdSlot.jsx";
import { TablaReferencia } from "../components/campos.jsx";
import { TOOLS } from "../data/tools.js";
import { CONTENIDO } from "../data/contenido.js";
import { TABLAS, TABLAS_POR_HERRAMIENTA, EJEMPLOS } from "../data/tablas.js";
import { VIGENCIA } from "../data/fiscal.js";

/* Los imports son estáticos a propósito. Con React.lazy, renderToString
   renderiza el fallback de Suspense en vez de la calculadora: el
   formulario no llegaba al HTML estático (Google veía un spinner) y el
   árbol del servidor no coincidía con el del cliente, lo que rompía la
   hidratación con el error #418. Las catorce juntas pesan ~46 KB. */
import Iva from "../tools/Iva.jsx";
import Infonavit from "../tools/Infonavit.jsx";
import SueldoNeto from "../tools/SueldoNeto.jsx";
import Interes from "../tools/Interes.jsx";
import Divisas from "../tools/Divisas.jsx";
import Medidas from "../tools/Medidas.jsx";
import Porcentajes from "../tools/Porcentajes.jsx";
import Descuentos from "../tools/Descuentos.jsx";
import Presupuesto from "../tools/Presupuesto.jsx";
import Negocio from "../tools/Negocio.jsx";
import Freelance from "../tools/Freelance.jsx";
import Imc from "../tools/Imc.jsx";
import Viaje from "../tools/Viaje.jsx";
import Obra from "../tools/Obra.jsx";
import Sugerir from "../tools/Sugerir.jsx";

const COMPONENTES = {
  tool_iva: Iva,
  tool_infonavit: Infonavit,
  tool_sueldo: SueldoNeto,
  tool_interes: Interes,
  tool_divisa: Divisas,
  tool_medidas: Medidas,
  tool_porcentaje: Porcentajes,
  tool_descuentos: Descuentos,
  tool_presupuesto: Presupuesto,
  tool_negocio: Negocio,
  tool_freelance: Freelance,
  tool_imc: Imc,
  tool_viaje: Viaje,
  tool_obra: Obra,
  tool_sugerir: Sugerir,
};

export default function ToolPage({ tool }) {
  const Calculadora = COMPONENTES[tool.id];
  const contenido = CONTENIDO[tool.id] ?? {};
  const faqs = contenido.faqs ?? [];
  const tablas = (TABLAS_POR_HERRAMIENTA[tool.id] ?? []).map((k) => TABLAS[k]?.()).filter(Boolean);
  const ejemplo = EJEMPLOS[tool.id];
  const relacionadas = TOOLS.filter((t) => t.category === tool.category && t.id !== tool.id && !t.seo.noindex).slice(0, 3);

  return (
    <>
      <Seo
        title={tool.seo.title}
        description={tool.seo.description}
        path={`/${tool.slug}`}
        noindex={tool.seo.noindex}
        jsonLd={jsonLdHerramienta(tool, faqs)}
      />

      <nav aria-label="Migas de pan" className="flex items-center gap-1 pt-6 text-xs texto-3">
        <Link to="/" className="transition hover:text-[color:var(--acento-texto)]">Inicio</Link>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="texto-2">{tool.category}</span>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="texto-2">{tool.name}</span>
      </nav>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-4">
          <div>
            <h1 className="text-[2rem] leading-[1.05] font-extrabold tracking-tight md:text-5xl">
              {tool.seo.h1}
            </h1>
            <p className="mt-2.5 max-w-2xl text-[15px] leading-relaxed texto-2">{tool.description}</p>
          </div>
        </div>
        <BotonCompartir tool={tool} />
      </header>

      <section aria-label="Calculadora" className="borde border-t pt-6">
        {Calculadora ? <Calculadora /> : null}
      </section>

      {contenido.vigencia !== false && (
        <p className="text-2xs texto-3">
          {VIGENCIA.etiqueta} · última revisión {VIGENCIA.revisadoEl}.
          {contenido.fuente ? ` Fuente: ${contenido.fuente}.` : ""}
        </p>
      )}

      {/* El ejemplo resuelto va pegado a la calculadora: sirve justo
          cuando la persona acaba de ver su número y quiere entenderlo. */}
      {ejemplo && (
        <section aria-label={ejemplo.titulo} className="tarjeta p-5 md:p-6">
          <h2 className="mb-4 text-base font-bold">{ejemplo.titulo}</h2>
          <ol>
            {ejemplo.pasos.map(([concepto, valor], i) => (
              <li
                key={i}
                className="borde flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b py-2.5 last:border-0"
              >
                <span className="texto-2 text-sm">
                  <span className="texto-3 mr-2 font-mono text-2xs">{i + 1}</span>
                  {concepto}
                </span>
                <span
                  className={`cifra font-mono text-sm ${
                    i === ejemplo.pasos.length - 1 ? "font-bold text-[color:var(--texto)]" : "texto-2"
                  }`}
                >
                  {valor}
                </span>
              </li>
            ))}
          </ol>
          {ejemplo.cierre && <p className="nota nota-aviso mt-4">{ejemplo.cierre}</p>}
        </section>
      )}

      {/* Justo bajo el resultado: el punto de mayor atención de la página. */}
      <AdSlot slot={RANURAS.resultado} className="w-full" />

      {tablas.length > 0 && (
        <section aria-label="Tablas de referencia" className="grid gap-5 lg:grid-cols-2">
          {tablas.map((t) => (
            <TablaReferencia key={t.titulo} {...t} />
          ))}
        </section>
      )}

      {contenido.secciones?.length > 0 && (
        <article className="w-full">
          {contenido.secciones.map((s, i) => (
            <section key={s.titulo} className="mb-10">
              <h2 className="mb-3 text-xl font-bold">{s.titulo}</h2>
              <div className="prosa space-y-3 text-[15px]">
                {s.parrafos.map((p, j) => (
                  <p key={j} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
              {s.lista && (
                <ul className="prosa mt-4 space-y-2 text-[15px]">
                  {s.lista.map((li, j) => (
                    <li key={j} className="flex gap-2">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--acento)]" aria-hidden="true" />
                      <span dangerouslySetInnerHTML={{ __html: li }} />
                    </li>
                  ))}
                </ul>
              )}
              {i === 0 && <AdSlot slot={RANURAS.contenido} className="mt-8" />}
            </section>
          ))}
        </article>
      )}

      {faqs.length > 0 && (
        <section className="w-full">
          <h2 className="mb-4 text-xl font-bold">Preguntas frecuentes</h2>
          <div className="divide-y divide-[color:var(--borde)] border-y borde">
            {faqs.map((f) => (
              <details key={f.pregunta} className="group py-4">
                <summary className="cursor-pointer list-none font-medium text-[color:var(--texto)] marker:content-none">
                  <span className="flex items-start justify-between gap-4">
                    {f.pregunta}
                    <ChevronRight
                      className="mt-0.5 h-4 w-4 shrink-0 texto-3 transition group-open:rotate-90"
                      aria-hidden="true"
                    />
                  </span>
                </summary>
                <p className="prosa mt-2.5 text-[15px]">{f.respuesta}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {relacionadas.length > 0 && (
        <section className="w-full pb-4">
          <h2 className="texto-2 mb-3 text-sm font-bold">
            También en {tool.category}
          </h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {relacionadas.map((t) => (
              <Link
                key={t.id}
                to={`/${t.slug}`}
                className="rounded-xl border borde superficie p-4 transition hover:border-[color:var(--color-marca-300)]"
              >
                <span className="text-sm font-semibold">{t.name}</span>
                <span className="texto-2 mt-1 block text-2xs">{t.shortDescription}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function BotonCompartir({ tool }) {
  const [copiado, setCopiado] = useState(false);

  async function compartir() {
    const url = `${SITIO}/${tool.slug}`;
    // En móvil, la hoja nativa de compartir funciona mejor que copiar.
    if (navigator.share) {
      try {
        await navigator.share({ title: tool.name, url });
        return;
      } catch {
        /* el usuario canceló: seguimos al portapapeles */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* sin permiso de portapapeles: no hacemos nada ruidoso */
    }
  }

  return (
    <button
      onClick={compartir}
      className="boton boton-suave self-start"
    >
      {copiado ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
      {copiado ? "¡Enlace copiado!" : "Compartir"}
    </button>
  );
}
